import { useMutation, useQuery } from '@apollo/client';
import { useAuth } from '../../../auth/AuthContext';
import {
  ADMIN_BANK_LINK_ECONOMICS_SUMMARY,
  ADMIN_MRR_SUMMARY,
  ADMIN_REFRESH_STRIPE_CACHE,
  ADMIN_SIGNUPS_BY_TIER_IN_RANGE,
} from '../../../graphql/adminQueries';
import { buildRange } from '../../../utils/dateRange';
import { adminEnvLabel } from '../../../utils/envCopy';
import { formatCurrency, formatDateTime, formatNumber } from '../../../utils/format';
import { compareModeledVsActual, formatMoney, formatPct } from '../model/engine';

const SIGNUP_MIX_ALLOWLIST = new Set(['free', 'basic', 'premium', 'enterprise', 'unknown']);
const SIGNUP_MIX_ORDER = ['free', 'basic', 'premium', 'enterprise', 'unknown', 'other'];
const SIGNUP_MIX_LABELS = new Map([
  ['unknown', 'Unknown / legacy'],
  ['other', 'Other'],
]);

export const ACQUISITION_MIX_CAPTION =
  'Last 28 UTC days — not the Growth range control. Includes unverified and deactivated accounts; not paying logos.';

/** Map a raw range-total slug to the display allowlist before any object key. */
export function collapseSignupMixSlug(slug) {
  const key = typeof slug === 'string' && slug.length > 0 ? slug : 'unknown';
  return SIGNUP_MIX_ALLOWLIST.has(key) ? key : 'other';
}

/** Collapse extra slugs to `other` using Map keys only (never `acc[row.slug]`). */
export function collapseSignupMixRows(rows) {
  const counts = new Map();
  const labels = new Map(SIGNUP_MIX_LABELS);

  for (const row of rows || []) {
    const slug = collapseSignupMixSlug(row?.slug);
    const n = Number(row?.count);
    const add = Number.isFinite(n) ? n : 0;
    counts.set(slug, (counts.get(slug) || 0) + add);
    if (slug !== 'other' && typeof row?.tier === 'string' && row.tier) {
      labels.set(slug, row.tier);
    }
  }

  const total = [...counts.values()].reduce((sum, n) => sum + n, 0);
  if (total <= 0) return [];

  const out = [];
  for (const slug of SIGNUP_MIX_ORDER) {
    const count = counts.get(slug);
    if (!count) continue;
    out.push({
      slug,
      tier: labels.get(slug) || slug,
      count,
      share: count / total,
    });
  }
  return out;
}

export function buildActualsSnapshot(mrr, bankLink) {
  if (!mrr && !bankLink) return null;

  return {
    asOf: mrr?.asOf || bankLink?.asOf,
    mrrCents: mrr?.mrrCents ?? null,
    activeSubscriptions: mrr?.activeSubscriptions ?? null,
    planBreakdown: (mrr?.planBreakdown || []).map((row) => ({
      tierSlug: row.tierSlug,
      count: row.count,
      mrrCents: row.mrrCents,
    })),
    bankLink: bankLink
      ? {
          activeItems: bankLink.activeItems,
          activeLinkers: bankLink.activeLinkers,
          linkersByTier: bankLink.linkersByTier,
          estimatedBalanceWavesMo: bankLink.estimatedBalanceWavesMo,
        }
      : null,
  };
}

function formatDelta(value, { asMoney = false, asPct = false } = {}) {
  if (value == null || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  const abs = Math.abs(value);
  if (asPct) return `${sign}${abs.toFixed(1)}%`;
  if (asMoney) return `${sign}${formatMoney(abs).replace(/^-/, '')}`;
  return `${sign}${abs}`;
}

export default function ActualsPanel({ projection, rates }) {
  const { isAdmin: canMutate } = useAuth();
  const modeledMonth = projection?.months?.[0] ?? null;

  const {
    data: mrrData,
    loading: mrrLoading,
    error: mrrError,
    refetch: refetchMrr,
  } = useQuery(ADMIN_MRR_SUMMARY);

  const {
    data: bankData,
    loading: bankLoading,
    error: bankError,
    refetch: refetchBank,
  } = useQuery(ADMIN_BANK_LINK_ECONOMICS_SUMMARY);

  const { data: mixData, error: mixError } = useQuery(ADMIN_SIGNUPS_BY_TIER_IN_RANGE, {
    variables: { range: buildRange(28) },
  });

  const [refreshStripe, { loading: refreshLoading, error: refreshError }] = useMutation(
    ADMIN_REFRESH_STRIPE_CACHE
  );
  const loading = mrrLoading || bankLoading || refreshLoading;
  const error = mrrError || bankError || refreshError;
  const mrr = mrrData?.adminMrrSummary;
  const bankLink = bankData?.adminBankLinkEconomicsSummary;
  const actuals = buildActualsSnapshot(mrr, bankLink);
  const comparison = compareModeledVsActual(modeledMonth, actuals, rates || {});

  const hasLiveData = mrr != null || bankLink != null;
  const handleRefresh = async () => {
    try {
      await refreshStripe();
      await Promise.all([refetchMrr(), refetchBank()]);
    } catch {
      // Apollo exposes the failure through refreshError below.
    }
  };
  const refreshButton = canMutate ? (
    <button
      type="button"
      className="secondary-button"
      disabled={refreshLoading}
      onClick={handleRefresh}
    >
      {refreshLoading ? 'Refreshing actuals…' : 'Refresh actuals'}
    </button>
  ) : null;

  if (loading) {
    return (
      <section className="chart-card economics-panel">
        <h2>Actuals vs model</h2>
        <p className="chart-hint">Loading live MRR and bank-link aggregates…</p>
        <div className="economics-preset-row">{refreshButton}</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="chart-card economics-panel">
        <h2>Actuals vs model</h2>
        <p className="module-status">
          Could not load actuals overlay. Confirm {adminEnvLabel()} API exposes{' '}
          <code>adminMrrSummary</code> and <code>adminBankLinkEconomicsSummary</code>.
        </p>
        <p className="chart-hint economics-muted">{error.message}</p>
        <div className="economics-preset-row">{refreshButton}</div>
      </section>
    );
  }

  if (!hasLiveData) {
    return (
      <section className="chart-card economics-panel">
        <h2>Actuals vs model</h2>
        <p className="module-status">
          No live data in cache yet. Stripe MRR cache refresh or bank links may be pending on{' '}
          {adminEnvLabel()}.
        </p>
        <div className="economics-preset-row">{refreshButton}</div>
      </section>
    );
  }

  const mixRows = mixError
    ? []
    : collapseSignupMixRows(mixData?.adminSignupsByTierInRange);

  const modeledRevenue =
    modeledMonth?.planRevenueP50 ??
    modeledMonth?.planRevenue ??
    modeledMonth?.revenueP50 ??
    modeledMonth?.revenue ??
    0;
  const modeledCogs = modeledMonth?.plaidCogsP50 ?? modeledMonth?.plaidCogs ?? 0;
  // Actuals provide MRR plus estimated Plaid COGS only; keep this comparison
  // on the same Plaid-only gross-margin basis as compareModeledVsActual.
  const modeledMargin = modeledRevenue - modeledCogs;
  const actualMrr = actuals.mrrCents != null ? actuals.mrrCents / 100 : null;

  return (
    <div className="economics-actuals">
      <section className="chart-card economics-panel">
        <h2>Actuals vs model</h2>
        <p className="chart-hint">
          Month 0 scenario compared to live Stripe MRR + anonymized bank-link aggregates (Class B
          only).
          {mrr?.asOf ? ` · MRR cache ${formatDateTime(mrr.asOf)}` : ''}
          {bankLink?.asOf ? ` · Bank links ${formatDateTime(bankLink.asOf)}` : ''}
        </p>
        <div className="economics-preset-row">{refreshButton}</div>

        <div className="kpi-grid">
          <article className="kpi-card">
            <p className="kpi-label">Modeled MRR</p>
            <p className="kpi-value">{formatMoney(modeledRevenue)}</p>
          </article>
          <article className="kpi-card">
            <p className="kpi-label">Actual MRR</p>
            <p className="kpi-value">
              {actuals.mrrCents != null
                ? formatCurrency(actuals.mrrCents, mrr?.currency || 'USD')
                : '—'}
            </p>
            <p className="kpi-hint">
              {actuals.activeSubscriptions != null
                ? `${formatNumber(actuals.activeSubscriptions)} paid subs`
                : 'Stripe MRR unavailable'}
            </p>
          </article>
          <article className="kpi-card">
            <p className="kpi-label">MRR Δ</p>
            <p className="kpi-value">{formatDelta(comparison.revenueDelta, { asMoney: true })}</p>
            <p className="kpi-hint">
              {comparison.mrrDeltaPct != null
                ? `${formatDelta(comparison.mrrDeltaPct, { asPct: true })} vs model`
                : '—'}
            </p>
          </article>
          <article className="kpi-card">
            <p className="kpi-label">Est. Plaid COGS (actual)</p>
            <p className="kpi-value">
              {comparison.estimatedActualPlaidCogs != null
                ? formatMoney(comparison.estimatedActualPlaidCogs)
                : '—'}
            </p>
            <p className="kpi-hint">
              {bankLink
                ? `${formatNumber(bankLink.activeItems)} items · ${formatNumber(
                    bankLink.estimatedBalanceWavesMo,
                  )} balance waves/mo`
                : 'Bank-link aggregates unavailable'}
            </p>
          </article>
        </div>
      </section>

      <section className="chart-card economics-panel">
        <h2>Variance (Plaid-only)</h2>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Metric</th>
                <th scope="col">Modeled</th>
                <th scope="col">Actual / est.</th>
                <th scope="col">Δ</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>MRR</td>
                <td>{formatMoney(modeledRevenue)}</td>
                <td>{formatMoney(actualMrr)}</td>
                <td>{formatDelta(comparison.revenueDelta, { asMoney: true })}</td>
              </tr>
              <tr>
                <td>Plaid COGS</td>
                <td>{formatMoney(modeledCogs)}</td>
                <td>
                  {comparison.estimatedActualPlaidCogs != null
                    ? formatMoney(comparison.estimatedActualPlaidCogs)
                    : '—'}
                </td>
                <td>{formatDelta(comparison.cogsDelta, { asMoney: true })}</td>
              </tr>
              <tr>
                <td>MRR less Plaid COGS</td>
                <td>{formatMoney(modeledMargin)}</td>
                <td>
                  {actualMrr != null && comparison.estimatedActualPlaidCogs != null
                    ? formatMoney(actualMrr - comparison.estimatedActualPlaidCogs)
                    : '—'}
                </td>
                <td>{formatDelta(comparison.marginDelta, { asMoney: true })}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {comparison.mixHint && (
          <p className="module-status economics-callout">{comparison.mixHint}</p>
        )}
      </section>

      {mixRows.length > 0 && (
        <section className="chart-card economics-panel">
          <h2>Acquisition mix (tier at signup)</h2>
          <p className="chart-hint">{ACQUISITION_MIX_CAPTION}</p>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Tier</th>
                  <th scope="col">Signups</th>
                  <th scope="col">Share</th>
                </tr>
              </thead>
              <tbody>
                {mixRows.map((row) => (
                  <tr key={row.slug}>
                    <td>{row.tier}</td>
                    <td>{formatNumber(row.count)}</td>
                    <td>{formatPct(row.share)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {bankLink?.linkersByTier?.length > 0 && (
        <section className="chart-card economics-panel">
          <h2>Bank links by tier</h2>
          <p className="chart-hint">
            {formatNumber(bankLink.activeLinkers)} active linkers ·{' '}
            {formatNumber(bankLink.activeItems)} Plaid items
          </p>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Tier</th>
                  <th scope="col">Linkers</th>
                  <th scope="col">Items</th>
                </tr>
              </thead>
              <tbody>
                {bankLink.linkersByTier.map((row) => (
                  <tr key={row.tierSlug}>
                    <td>{row.tierSlug}</td>
                    <td>{formatNumber(row.users)}</td>
                    <td>{formatNumber(row.items)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <p className="module-footnote">
        Class B aggregates only — no tokens, balances, or institution names. COGS estimate uses the
        scenario rate card and sync-frequency wave model.
      </p>
    </div>
  );
}
